import { Box, Tooltip, Chip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { useData } from "@pure-ptr/react";
import { useSelector } from "react-redux";
import React, { useMemo } from "react";
import { pageStore, renderHoverLink, useGlobalStore, XPagedDataGrid } from "../../common";
import { getPlaceOfServiceCodes } from "../../store/api/claimAnalysis";
import { CONTRACTED_SERVICE_INPATIENT, CONTRACTED_SERVICE_OUTPATIENT, CONTRACTED_SERVICE_PROFESSIONAL } from "../../store/constants/contract";
import { BooleanEquators, dateEquators, integerEquatorsForEqualsAndBetween, integerEquators, StringEquators } from "../../store/constants/pages";
import { asDate } from "../../util/date";
import { createClaimAnalysisPatientNameField, createBooleanDataField,createClaimAnalysisPhysicianNameField,createTimestampField, createClaimAnalysisStatusField, createClaimAnalysisSubscriberNameField, createClaimNumberFieldWithCopyButton, createCommentsField, createCurrencyDataField, createDateField, createIconDataField, createNumberDataField, createNullableNumberDataField, createPlainDateField, createStringDataField,createConditionalStringDataField } from "../../util/format";
import { getValidUsers } from "../../store/api/claimAssignment";
import { RootState } from "../../store/reducers";
import { selectIsSuperAdmin, selectHasPatternIntelligenceAccess } from "../../store/selectors/session";
import { getRemitPayersTiny } from "../../store/api/payers";
import { fromSessionStorage } from "../../util/storage";
import TriagePopoverLink from "../ConfidenceScoreTriagePage/TriagePopoverLink";

export const ClaimAnalysisDataGrid = ({ 
    selectedCriteria,
    refreshCriteriaList, filtersPtr, paginationFilters, sortPtr, paginationPtr, selectedClaimsIdsPtr, loading, data, handleSingleFlag, handleAcknowledge,showSearchFilters,
    defaultCustomFilter,rcmAssignmentData=[],filteredUsers
 }) => {
    const contractCustom=filtersPtr.value.customFilter?.find(x => x?.field === 'contract.contractState.name')?.value ?? 'both'

    const claimActionAppliedFilter =
    filtersPtr.value.filter?.find(
        (x) => x?.field === "isClaimActionApplied"
    ) ||
    filtersPtr.value.customFilter?.find(
        (x) => x?.field === "isClaimActionApplied"
    );

   const isClaimActionAppliedEnabled = 
    (claimActionAppliedFilter?.equator === "equals" && 
     (claimActionAppliedFilter?.value === true || claimActionAppliedFilter?.value === "true")) ||
    (claimActionAppliedFilter?.equator === "not_equal_to" && 
     (claimActionAppliedFilter?.value === false || claimActionAppliedFilter?.value === "false"));


    const columnDefs = useColumnDefinitions(rcmAssignmentData, filtersPtr.value.customFilter,contractCustom, filteredUsers,isClaimActionAppliedEnabled, filtersPtr);
    const actions = params => [
        <GridActionsCellItem
            icon={
            <Tooltip title={params.row.hasExecutionError ? params.row.executionError : ''}>
                {params.row.hasExecutionError ? (
                    <i className="fas fa-exclamation-circle text-danger"></i>
                ) : (
                    <i className="fas fa-exclamation-circle text-secondary"></i>
                )}
            </Tooltip>
            }
            label="Execution Error"
            onClick={() => {}}
        />,
        <GridActionsCellItem
            icon={
            <Tooltip title={params.row.flag ? 'Unfollow' : 'Follow'}>
                {params.row.flag ? (
                    <i className="fa fa-flag text-danger"></i>
                ) : (
                    <i className="fa fa-flag text-secondary"></i>
                )}
            </Tooltip>
            }
            label="Follow"
            onClick={() => handleSingleFlag(!params.row.flag, params.row)}
        />,

        <GridActionsCellItem
            icon={
            <Tooltip title={params.row.acknowledged ? 'Unacknowledge' : 'Acknowlege'}>
                {params.row.acknowledged ? (
                    <i className="fas fa-check-circle text-success"></i>
                ) : (
                    <i className="fas fa-check-circle text-secondary"></i>
                )}
            </Tooltip>
            }
            label="Acknowledge"
            onClick={() => handleAcknowledge(params.row)}
        />,
    ];

    return <>
        <XPagedDataGrid
            fields={columnDefs}
            actions={actions}

            data={data}
            loading={loading}

            selectedIdsPtr={selectedClaimsIdsPtr}
            showSearchFilters={showSearchFilters}
            paginationFilters={ paginationFilters }

            paginationPtr={paginationPtr}
            sortPtr={sortPtr}
            filtersPtr={filtersPtr}

            refreshCriteriaList={refreshCriteriaList}
            selectedCriteria={selectedCriteria}
            defaultCustomFilter={defaultCustomFilter}

            name="claimAnalysis-landing"
            defaultSort={DEFAULT_SORT}
            defaultHiddenFields={DEFAULT_HIDDEN_FIELDS}
            criteriasSaveEnabled={true}
        />
    </>
}

const DEFAULT_SORT = [{ id: 'variance', direction: 'desc' }];
const DEFAULT_HIDDEN_FIELDS = ['payer.name', 'updatedAt', 'recoveredAmount', 'physicianLastname', 'age','patientMrn','patientGender','SnResponsecontactlastororganizationname','subscriberRelationship','subscriberPolicyNumber','subscriberGroupName','remitTraceNumber','snResponsecontactidentifier','confidenceScore','isConsolidatedClaim'];
const { usePageStore } = pageStore('claimAnalysis-landing');

const useColumnDefinitions = (rcmAssignmentData, customFilter,contractCustom, filteredUsers,isClaimActionAppliedEnabled, filtersPtr) => {
    const tinyContracts = useGlobalStore( x => x.contracts.tiny );
    const tinyPayerContracts = useGlobalStore( x => x.payers.payerContracts );
    const recoveredAmountFlag = useGlobalStore( x => x.settings.recoveredAmountFlag );
    const remitLinesSettings=useGlobalStore( x => x?.settings?.data?.find((item)=>item?.name?.toLowerCase() === "only unmatched remit lines")?.value );
    const claimStatuses = useGlobalStore( x => x.claimAnalysis.claimStatuses ?? [] );
    const facilities = useGlobalStore( x => x.facilities.data ?? [] );
    const dischargeStatusCodes = useGlobalStore( x => x.rules.dischargeStatusCodes ?? [] );

    const { data:placeOfServiceCodes, isPending, error } = useData( async () => {        
        const placeOfServiceCodes = await getPlaceOfServiceCodes();
        return placeOfServiceCodes
    }, []);

    const { data:remitPayers } = useData( async () => {        
        const remitPayers = await getRemitPayersTiny();
        return remitPayers
    }, []);
    
    const npiRoles = useGlobalStore( x => 
        x.npiRoles.data?.map(
            item => ({ 
                label: item?.name, 
                value: item?.name, 
                name: item?.name 
            })
        )
    );
    
    const sortByUsername = users => users?.sort((a, b) => (a.username || '').localeCompare(b.username || '', undefined, { sensitivity: 'base' }));
    const hasEAPGAcces=useSelector<RootState>((state)=>state?.clients?.hasEAPGAccess )
    const codeServices:any=useSelector<RootState>((state)=>state?.services?.tiny )
    const isSuperAdmin = useSelector(selectIsSuperAdmin);
    const hasPatternIntelligenceAccess = useSelector(selectHasPatternIntelligenceAccess);

    const renderConfidenceScoreHoverLink = (hoverData) => {
        return (params) => (
            params.value !== null ?
            (<TriagePopoverLink
                columnData={params.value}
                title={''}
                hoverData={hoverData(params.row)}
            />) : 'N/A'
        )
    }
    
    const handlePatternNameClick = (patternName) => {
        const newFilter = {
            field: 'ClaimPatternAssignment.PatternName',
            equator: 'equals',
            value: patternName
        };
        filtersPtr.update(prev => {
            const currentFilters = prev.filter || [];
            const otherFilters = currentFilters.filter(f => f.field !== 'ClaimPatternAssignment.PatternName');
            return {
                ...prev,
                filter: [...otherFilters, newFilter]
            };
        });
    };

    const handleClaimCountClick = (count) => {
        const newFilter = {
            field: 'claimcount',
            equator: 'equals',
            value: count
        };
        filtersPtr.update(prev => {
            const currentFilters = prev.filter || [];
            const otherFilters = currentFilters.filter(f => f.field !== 'claimCount');
            return {
                ...prev,
                filter: [...otherFilters, newFilter]
            };
        });
    };

    return useMemo(() => {
        const paidStatus = customFilter?.find((item) => item?.field === "isPaid"),
        isUnpaid = paidStatus?.value === "false";

        let fields = [
            createClaimNumberFieldWithCopyButton('claimnumber', 'Claim Number', { returnWholeRow: true, sortable: true, equators: StringEquators, group: 'Claim Information', positionFixed: true, reorder: false, customCell: true, hideable: false  }),
            createStringDataField('renderingnpi', 'NPI Number', { sortable: true, equators: StringEquators, group: 'Claim Information', hideFromGrid: true, reorder: false }),
            createNumberDataField('age', 'Patient Age', { sortable: true, equators: integerEquators, group: 'Claim Information' }),
            createNullableNumberDataField('confidenceScore', 'Confidence Score', { sortable: true, equators: integerEquators, group: 'Claim Information', hideFromGrid: !isSuperAdmin, hideFromFilter: !isSuperAdmin,
                renderCell: renderConfidenceScoreHoverLink(row => row?.measureXref ?? []),
                valueFormatter: (value) => value === null || value === undefined ? 'N/A' : `${value} %`
            }),

            createClaimAnalysisPatientNameField('patientLastname', 'Patient Name', { sortable: true, equators: StringEquators, returnWholeRow: true, hideFromFilter: true, width: '100px' }),
            createClaimAnalysisPhysicianNameField('physicianLastname', 'Physician Name', { sortable: true, equators: StringEquators, returnWholeRow: true, hideFromFilter: true, width: '100px' }),
            createStringDataField('payerClaimControlNumber', 'Payer Claim Control Number', { sortable: true, equators: StringEquators, group: 'Claim Information', hideFromGrid: false, reorder: false }),
            createStringDataField('patientFirstname', 'Patient First Name', { sortable: true, equators: StringEquators, group: 'Claim Information', hideFromGrid: true, reorder: false }),
            createStringDataField('patientLastname', 'Patient Last Name', { sortable: true, equators: StringEquators, group: 'Claim Information', hideFromGrid: true, reorder: false }),
            createStringDataField('physicianFirstname', 'Physician First Name', { sortable: true, equators: StringEquators, group: 'Claim Information', hideFromGrid: true, reorder: false }),
            createStringDataField('physicianLastname', 'Physician Last Name', { sortable: true, equators: StringEquators, group: 'Claim Information', hideFromGrid: true, reorder: false }),
            createStringDataField('rule.name', 'Rule Name', { sortable: true, equators: StringEquators, secondLevel: 'name', group: 'Charge Information', hideFromGrid: true, reorder: false }),
            createStringDataField('contract.name', 'Contract Name', { 
                sortable: true, 
                equators: StringEquators, 
                secondLevel: 'name', 
                onHover: true, 
                group: 'Claim Information', 
                isDropdown: true,
                isMultiSelection: true,
                dropdownValues:contractCustom==="both" ? tinyContracts?.map((item)=>item?.name):tinyContracts.reduce((result, contract) => contract.state?.toLowerCase() === contractCustom ? [...result, contract.name] : result, []),
                width: '100px',

                renderCell: renderHoverLink(
                    row => [
                        {
                            label: "Name",
                            data: row?.contract?.name,
                        }, {
                            label: "Effective Date",
                            data: asDate(row?.contract?.effectiveDate),
                        }, {
                            label: "Termination Date",
                            data: asDate(row?.contract?.terminationDate),
                        }, {
                            type: "LINK",
                            label: "Go to Contracts",
                            data: `/cms/contract/view/${row?.contract?.id}`,
                        },
                    ]
                )
            }),
                 createStringDataField('payerContract.displayName', 'Payer Contract', { 
                sortable: true, 
                equators: StringEquators, 
                secondLevel: 'name', 
                onHover: true, 
                group: 'Claim Information', 
                isDropdown: true,
                isMultiSelection: true,
                dropdownValues:tinyPayerContracts?.map((item)=>item?.displayName),
                width: '100px',

            }),
            
            createConditionalStringDataField('payer.name', 'Payer Name','usePlanIdMapping','planPayerContract.payerName', { sortable: true, equators: StringEquators, secondLevel: 'name', group: 'Claim Information', width: '100px' }),
            createPlainDateField('servicedate', 'Service Date', { sortable: true, equators: dateEquators, group: 'Claim Information', whiteSpace: 'pre' }),
            createPlainDateField('remitDate', 'Remit Date', { sortable: true, equators: dateEquators, group: 'Claim Information', whiteSpace: 'pre', hideFromFilter: isUnpaid }),
            createDateField('followupAlert', 'Follow Up Date', {  equators: dateEquators, group: 'Claim Information',hideFromFilter:true }),
            createStringDataField('visit', 'Visit', { sortable: true, equators: StringEquators, group: 'Claim Information', isDropdown: true, isMultiSelection: true, dropdownValues: visitTypes as any }),
            createStringDataField('estimatedPaymentDetails.principaldx', 'Principal Diagnosis', { sortable: true, equators: StringEquators, secondLevel: 'principaldx', group: 'Claim Information', hideFromFilter: true }),
            createStringDataField('principaldx', 'Principal Diagnosis', { sortable: true, equators: StringEquators, group: 'Claim Information', hideFromGrid: true }),
            createStringDataField('interchangeControlNumber', 'Interchange Control Number', { sortable: true, equators: StringEquators, group: 'Claim Information' }),
            createStringDataField('patientMrn', 'Patient MRN', { sortable: true, equators: StringEquators, group: 'Claim Information' }),
            createStringDataField('patientGender', 'Patient Gender', { sortable: true, equators: StringEquators, group: 'Claim Information' }),

            createClaimAnalysisSubscriberNameField('SnResponsecontactlastororganizationname', 'Subscriber Full Name', { sortable: true, equators: StringEquators, returnWholeRow: true, hideFromFilter: true, width: '100px' }),

            createStringDataField('subscriberRelationship', 'Subscriber Relationship', { sortable: true, equators: StringEquators, group: 'Claim Information' }),
            createStringDataField('subscriberPolicyNumber', 'Subscriber Policy Number', { sortable: true, equators: StringEquators, group: 'Claim Information' }),
            createStringDataField('subscriberGroupName', 'Subscriber Group Name', { sortable: true, equators: StringEquators, group: 'Claim Information' }),
        
            createStringDataField('remitTraceNumber', 'Remit Trace Number', { sortable: true, equators: StringEquators, group: 'Claim Information' }),
            createStringDataField('snResponsecontactidentifier', 'MemberId', { sortable: true, equators: StringEquators, group: 'Claim Information' }),
            
            createStringDataField('snResponsecontactfirstname', 'Subscriber First Name', { sortable: true, equators: StringEquators, group: 'Claim Information', hideFromGrid: true, reorder: false }),
            createStringDataField('snResponsecontactlastororganizationname', 'Subscriber Last Name', { sortable: true, equators: StringEquators, group: 'Claim Information', hideFromGrid: true, reorder: false }),
            createNumberDataField('claimcount', 'Similar Claims', { 
                sortable: true, 
                equators: integerEquators, 
                group: 'Claim Information',
                field: 'ClaimPatternAssignment.PatternCluster.ClaimCount',
                renderCell: (params) => (
                    <Box 
                        component="span" 
                    >
                        {params.row.claimCount}
                    </Box>
                )
            }),
            createStringDataField('ClaimPatternAssignment.PatternName', 'Pattern Group', { 
                sortable: true, 
                equators: StringEquators, 
                group: 'Claim Information',
                width: '250px',
                renderCell: (params) => params.row.patternGroup ? (
                    <Tooltip title={params.row.patternGroup}>
                    <Chip 
                        label={params.row.patternGroup} 
                        size="small" 
                        variant="outlined" 
                        onClick={() => handlePatternNameClick(params.row.patternGroup)}
                        sx={{ 
                            borderRadius: '4px',
                            fontWeight: 500,
                            borderColor: '#e1f0ff',
                            color: '#181c32',
                            backgroundColor: '#ffffff',
                            height: '24px',
                            '& .MuiChip-label': { px: 1 }
                        }} 
                    />
                    </Tooltip>
                ) : null
            }),
            // RCM Fields 
            createStringDataField('npirolename', 'Provider Roles', { equators: StringEquators, hidden: true, isStrictDropdown: true, isMultiSelection: true, dropdownValues: npiRoles, group: 'Claim Information' }),
            createCurrencyDataField('chargedAmount', 'Charged Amount', { sortable: true, equators: integerEquators, group: 'Claim Information', hideFromFilter: isUnpaid }),
            createCurrencyDataField('expectedPay', 'Expected Pay', { sortable: true, equators: integerEquators, group: 'Claim Information' }),
            createCurrencyDataField('actualPay', 'Allowable', { sortable: true, equators: integerEquators, group: 'Claim Information', hideFromFilter: isUnpaid }),
            createCurrencyDataField('nonCoveredAmount', 'Non-Covered', { sortable: true, equators: integerEquators, group: 'Claim Information', hideFromFilter: isUnpaid }),
            createCurrencyDataField('variance', 'Variance', { sortable: true, equators: integerEquators, labelStyle: 'label label-lg label-inline', group: 'Claim Information', hideFromFilter: isUnpaid }),
            createStringDataField('drg', 'Drg', { equators: StringEquators, hidden: true, group: 'Claim Information' }),
            createStringDataField('icd10PcsCode', 'ICD10-PCS Code', { sortable: true, equators: StringEquators, group: 'Claim Information' }),
            createStringDataField('revcode', 'Revenue Code', { equators: StringEquators, hidden: true, group: 'Charge Information' }),
            createStringDataField('pxcode', 'Procedure Code', { equators: StringEquators, hidden: true, group: 'Charge Information' }),
            createStringDataField('reasoncode', 'CAS Code', { equators: StringEquators, hidden: true, group: 'Charge Information', hideFromFilter: isUnpaid }),
            createStringDataField('casCodes', 'CAS Codes', { equators: StringEquators, group: 'Charge Information', hideFromFilter: true }),
            createStringDataField('eapgCode', 'EAPG', { equators: StringEquators, group: 'Claim Information' ,hideFromFilter:true,hideFromGrid:!hasEAPGAcces}),
            createStringDataField('codeserviceid', 'Code Service', { equators: BooleanEquators, group: 'Charge Information' ,hideFromGrid:true,isStrictDropdown: true, isMultiSelection: true, dropdownValues:codeServices?.map((item)=>({label:item?.name,value:item?.id})) || []}),

            createStringDataField('remitPayer', 'Remit Payer Name', { equators: StringEquators, group: 'Claim Information',showNA:true,hideFromFilter:true}),
            createStringDataField('payername', 'Remit Payer Name', {
                equators: StringEquators, group: 'Claim Information', hideFromGrid: true,
                isDropdown: true,
                isMultiSelection: true,
                dropdownValues: remitPayers?.map((item) => item?.name),

            }),

            createStringDataField('code', 'EAPG', { equators: integerEquatorsForEqualsAndBetween, group: 'Claim Information' ,hideFromFilter:!hasEAPGAcces,hideFromGrid:true}),
            createClaimAnalysisStatusField('status', 'Status', { sortable: false, hideFromFilter:true, returnWholeRow: true, equators: StringEquators, group: 'Claim Information' }),
            createStringDataField('rcmTeam', 'RCM Team', { equators: StringEquators, group: 'Claim Information',hideFromFilter:true}),
            createStringDataField('rcmTeamId', 'RCM Team', { equators: BooleanEquators, group: 'Claim Information' ,isStrictDropdown: true, isMultiSelection: true, dropdownValues:rcmAssignmentData?.map((item)=>({label:item?.name,value:item?.id})) || [],hideFromGrid:true}),
            createStringDataField('claimStatusId', 'Status',{ equators: BooleanEquators, hidden: true, isStrictDropdown:true, isMultiSelection: true, dropdownValues: claimStatuses?.map((item)=> ({label:item.name,value:item.id,name:item.name})), group: 'Claim Information'}),
            createStringDataField('placeOfServiceCode', 'Place Of Service',{ equators: BooleanEquators, hidden: true, isStrictDropdown:true, isMultiSelection: true, dropdownValues: placeOfServiceCodes?.map((item)=> ({label:item.name,value:item.code,name:item.code})), group: 'Claim Information'}),
            createStringDataField('billingProviderNpi', 'Billing Provider NPI', { equators: StringEquators, hidden: true, group: 'Claim Information' }),
            createStringDataField('dischargestatus', 'Discharge Status', { equators: BooleanEquators, hidden: true, isStrictDropdown: true, isMultiSelection: true, dropdownValues: dischargeStatusCodes.map(item => ({ label: `${item.code} - ${item.description}`, value: item.code, name: item.code })), group: 'Claim Information' }),
            createDateField('updatedAt', 'Updated At', { whiteSpace: 'pre', sortable: true, equators: dateEquators, group: 'Charge Information' }),
            createStringDataField('isClaimActionApplied', 'Claim Action Applied', { equators: BooleanEquators, hideFromGrid: true, isStrictDropdown: true, dropdownValues: claimActionAppliedDropdownValues, group: 'Claim Information' }),
            createBooleanDataField('isConsolidatedClaim','Consolidated Claim',{equators: BooleanEquators,hideFromGrid: true,group: 'Claim Information',isStrictDropdown: true,dropdownValues: [{ label: 'Yes', value: true },{ label: 'No', value: false }]}),
            createBooleanDataField('isPaid', 'Paid', {
                equators: BooleanEquators,
                group: 'Claim Information',
                isStrictDropdown: true, 
                dropdownValues: denailDropdownValues,
                hideFromFilter: true,
            }),

            createBooleanDataField(
            'hasExecutionError',
            'Has Execution Error',
            {
                equators: BooleanEquators,
                group: 'Claim Information',
                hideFromGrid: true,
                isStrictDropdown: true,
                dropdownValues: [
                    { label: 'Yes', value: true },
                    { label: 'No', value: false }
                ]
            }
        ),
            createIconDataField('isDenial', 'Denial', { 
                equators: BooleanEquators, 
                isStrictDropdown: true, 
                dropdownValues: denailDropdownValues, 
                icon: "fas fa-times-circle", 
                checkedClass: 'text-danger', 
                group: 'Claim Information', 
                hideFromFilter: isUnpaid,

                renderCell: (params) =>
                    <Box className={`fas fa-times-circle ${ params.value ? 'text-danger' : 'text-secondary' } ml-2 null font-14`} />
            }),

            createStringDataField('hasUnmatchedRemitLines', 'Only Show Unmatched Remit Lines', { 
                equators: {...BooleanEquators,equatorArr:BooleanEquators?.equatorArr?.filter(item => item?.value === 'equals')}, 
                isStrictDropdown: true, 
                dropdownValues: onlyUnmatchedRemitLinesValues, 
                group: 'Charge Information', 
                hideFromGrid:true,
                hideFromFilter:remitLinesSettings!='true'
            }),

            createStringDataField('assignedTo', 'Assigned User', { 
                equators: StringEquators, 
                isDropdown: true,
                isMultiSelection: true,
                alternateNoneLabel:"Un-Assigned",
                dropdownValues: sortByUsername(filteredUsers)?.map((item)=>item?.username) || [], 
                // icon: "fas fa-times-circle", 
                // checkedClass: 'text-danger', 
                group: 'Claim Information', 
            }),
            createStringDataField('facilityId', 'Facility', { equators: BooleanEquators, hidden: true, group: 'Claim Information', isStrictDropdown:true, isMultiSelection: true, dropdownValues: facilities?.map((item)=>({label:item?.name,value:item?.id,name:item?.name})) }),
            createCommentsField('comment', 'Comment(s)', { sortable: false, equators: StringEquators, hideFromFilter: true, group: 'Claim Information' }),

            createBooleanDataField('acknowledged', 'Acknowledged', { equators: BooleanEquators,group: 'Charge Information', hideFromFilter: true }),
            createBooleanDataField('flag', 'Flagged', { equators: BooleanEquators,  group: 'Charge Information', hideFromFilter: true }),
            createPlainDateField('patientDob', 'Patient DOB', { sortable: true, equators: dateEquators, returnWholeRow: true, hideFromFilter: true, width: '100px' }),
            createCurrencyDataField('otherAllowable', 'Other Allowable', { sortable: true, equators: integerEquators, labelStyle: 'label label-lg label-inline', group: 'Claim Information', hideFromFilter: true }),
            createCurrencyDataField('patientResponsibility', 'Patient Responsibility', { sortable: true, equators: integerEquators, labelStyle: 'label label-lg label-inline', group: 'Claim Information', hideFromFilter: true }),

        ];  
           if (isClaimActionAppliedEnabled) {
            fields = fields.map(field => {
        if (field.id === 'isPaid' || field.id === 'isDenial') {
            return {
                ...field,
                options: {
                    ...field.options,
                    disabled: true,
                    readOnly: true,
                    editable: false
                },
                xDataField: {
                    ...field.xDataField,
                    editable: false
                }
            };
        }
        return field;
    });
}

        if (recoveredAmountFlag) {
            const insertIndex = fields.findIndex(field => field.id === 'variance') + 1;
            fields.splice(insertIndex, 0,
                createCurrencyDataField('recoveredAmount', 'Recovered Amount', { sortable: true, equators: integerEquators, labelStyle: 'label label-lg label-inline', group: 'Claim Information', hideFromFilter: isUnpaid })
            );
        }

        if (!hasPatternIntelligenceAccess) {
            fields = fields.filter(field => field.id !== 'claimcount' && field.id !== 'ClaimPatternAssignment.PatternName');
        }
        
        // Inserting columns saved width to options, So grid can pick width from options if resized and exists
        const claimAnalysisColumnnsWidths = fromSessionStorage(`claimAnalysis-landing-column-widths`)  || {};
        fields = fields?.map((item) => {
            const columnWidth = claimAnalysisColumnnsWidths[item?.id];
            if (columnWidth) {
                return {
                    ...item,
                    options: {
                        ...item?.options,
                        width: columnWidth,
                    },
                    xDataField: {
                        ...item?.xDataField,
                        width: columnWidth,
                    },
                };
            }
            return item;
        });

        return fields;
    }
    , [customFilter, claimStatuses, npiRoles, recoveredAmountFlag, tinyContracts,tinyPayerContracts, rcmAssignmentData, hasEAPGAcces,contractCustom,codeServices,remitLinesSettings,isClaimActionAppliedEnabled,isSuperAdmin, filtersPtr, handleClaimCountClick, hasPatternIntelligenceAccess,remitPayers]);
};

const visitTypes = [
    CONTRACTED_SERVICE_INPATIENT,
    CONTRACTED_SERVICE_OUTPATIENT,
    CONTRACTED_SERVICE_PROFESSIONAL
];

const denailDropdownValues = [{
    label: 'Yes',
    value: 'true'
}, {
    label: 'No',
    value: 'false'
}]

const onlyUnmatchedRemitLinesValues = [{
    label: 'Yes',
    value: true
}, {
    label: 'No',
    value: false
}]

const claimActionAppliedDropdownValues = [
    {
        label: "Yes",
        value: true,
    },
    {
        label: "No",
        value: false,
    },
];